'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  Warehouse, TrendingUp, TrendingDown, DollarSign, Package, 
  Loader2, AlertTriangle, Users, Activity
} from 'lucide-react';

import { DataTable } from '@/components/dashboard/DataTable';
import { 
  fetchStorageMetrics,
  formatCurrency
} from '@/lib/api-client';
import type { TableColumn } from '@/types/dashboard';

const COLORS = ['#3B82F6', '#22C55E', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316'];

// Card Component
const Card = ({ children, className = "", isDark = true }: { children: React.ReactNode; className?: string; isDark?: boolean }) => (
  <div className={`backdrop-blur-md border rounded-xl p-5 transition-colors duration-300 ${
    isDark 
      ? 'bg-neutral-900/50 border-neutral-800' 
      : 'bg-white border-gray-200 shadow-sm'
  } ${className}`}>
    {children}
  </div>
);

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isDark = true }: any) => (
  <Card isDark={isDark}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className={`text-sm font-medium ${isDark ? 'text-neutral-400' : 'text-gray-600'}`}>
          {title}
        </p>
        <h3 className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {value}
        </h3>
        {subtitle && (
          <p className={`text-xs mt-1 ${isDark ? 'text-neutral-500' : 'text-gray-500'}`}>
            {subtitle}
          </p>
        )}
      </div>
      {Icon && (
        <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
          <Icon size={20} className="text-blue-500" />
        </div>
      )}
    </div>
    {trend !== undefined && (
      <div className={`mt-3 flex items-center gap-1 text-sm ${
        trend >= 0 ? 'text-emerald-400' : 'text-rose-400'
      }`}>
        {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
        <span>{Math.abs(trend)}% vs last period</span>
      </div>
    )}
  </Card>
);

export default function WarehousePage() {
  const [storageData, setStorageData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode] = useState(true);

  // Load warehouse data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        
        const data = await fetchStorageMetrics(12);
        
        setStorageData(data);
        console.log('✅ Loaded warehouse data:', data.length, 'months');
      } catch (err) {
        console.error('❌ Failed to load warehouse data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!storageData.length) return null;

    const totalRevenue = storageData.reduce((sum, month) => sum + (month.storage_revenue || 0), 0);
    const totalUnitsReceived = storageData.reduce((sum, month) => sum + (month.units_received || 0), 0);
    const totalTransactions = storageData.reduce((sum, month) => sum + (month.receiving_transactions || 0), 0);
    const avgRevenuePerMonth = totalRevenue / storageData.length;

    // Calculate trend (last 3 months vs previous 3 months)
    const recent = storageData.slice(-3);
    const previous = storageData.slice(-6, -3);
    
    if (previous.length >= 3 && recent.length >= 3) {
      const recentAvg = recent.reduce((sum, m) => sum + (m.storage_revenue || 0), 0) / 3;
      const prevAvg = previous.reduce((sum, m) => sum + (m.storage_revenue || 0), 0) / 3;
      const trend = ((recentAvg - prevAvg) / prevAvg) * 100;

      return {
        totalRevenue,
        totalUnitsReceived,
        totalTransactions,
        avgRevenuePerMonth,
        trend: Math.round(trend * 10) / 10,
        activeClients: storageData[storageData.length - 1]?.active_clients || 0
      };
    }

    return {
      totalRevenue,
      totalUnitsReceived,
      totalTransactions,
      avgRevenuePerMonth,
      trend: 0,
      activeClients: storageData[storageData.length - 1]?.active_clients || 0
    };
  }, [storageData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return storageData.map(month => ({
      month: new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      revenue: month.storage_revenue || 0,
      unitsReceived: month.units_received || 0,
      transactions: month.receiving_transactions || 0,
      activeClients: month.active_clients || 0
    }));
  }, [storageData]);

  // Table columns
  const storageColumns: TableColumn[] = [
    {
      key: 'month',
      label: 'Month',
      sortable: true
    },
    {
      key: 'storage_revenue',
      label: 'Storage Revenue',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    },
    {
      key: 'units_received',
      label: 'Units Received',
      sortable: true,
      align: 'right',
      formatter: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'receiving_transactions',
      label: 'Transactions',
      sortable: true,
      align: 'right',
      formatter: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'active_clients',
      label: 'Active Clients',
      sortable: true,
      align: 'right'
    },
    {
      key: 'avg_revenue_per_client',
      label: 'Avg/Client',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className={isDarkMode ? 'text-neutral-400' : 'text-gray-600'}>
            Loading warehouse analytics...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Failed to load data
          </p>
          <p className={isDarkMode ? 'text-neutral-400' : 'text-gray-600'}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Warehouse & Storage
        </h1>
        <p className={`mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
          Storage revenue, receiving activity, and warehouse utilization
        </p>
      </div>

      {/* Summary Metrics */}
      {summaryMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Storage Revenue"
            value={formatCurrency(summaryMetrics.totalRevenue)}
            subtitle="Last 12 months"
            icon={DollarSign}
            trend={summaryMetrics.trend}
            isDark={isDarkMode}
          />
          <MetricCard
            title="Units Received"
            value={summaryMetrics.totalUnitsReceived.toLocaleString()}
            subtitle="Total units"
            icon={Package}
            isDark={isDarkMode}
          />
          <MetricCard
            title="Transactions"
            value={summaryMetrics.totalTransactions.toLocaleString()}
            subtitle="Receiving transactions"
            icon={Activity}
            isDark={isDarkMode}
          />
          <MetricCard
            title="Active Clients"
            value={summaryMetrics.activeClients}
            subtitle="Using storage"
            icon={Users}
            isDark={isDarkMode}
          />
        </div>
      )}

      {/* Charts Row 1: Revenue & Volume Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Storage Revenue Trend */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Storage Revenue Trend
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="month" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => formatCurrency(value)}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#3B82F6" 
                fill="#3B82F6"
                fillOpacity={0.6}
                name="Storage Revenue"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Units Received Trend */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Units Received Trend
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="month" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => value.toLocaleString()}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="unitsReceived" 
                stroke="#22C55E" 
                strokeWidth={2}
                dot={{ fill: '#22C55E', r: 3 }}
                name="Units Received"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts Row 2: Activity Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Receiving Transactions */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Receiving Transactions
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="month" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => value.toLocaleString()}
              />
              <Bar dataKey="transactions" fill="#8B5CF6" name="Transactions" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Active Clients Trend */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Active Storage Clients
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="month" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="activeClients" 
                stroke="#F59E0B" 
                fill="#F59E0B"
                fillOpacity={0.6}
                name="Active Clients"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Monthly Detail Table */}
      <Card isDark={isDarkMode}>
        <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Monthly Storage Metrics
        </h2>
        <DataTable
          columns={storageColumns}
          data={storageData.map(month => ({
            ...month,
            month: new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            avg_revenue_per_client: (month.storage_revenue || 0) / (month.active_clients || 1)
          }))}
          pageSize={12}
          searchable={false}
          isDark={isDarkMode}
        />
      </Card>
    </div>
  );
}
